import type { Edge, Node } from '../models/scene';
import { expandNodeIdsWithGroupMembers } from '../../lib/diagram/grouping';

export type ClipboardPoint = { x: number; y: number };

export const CLIPBOARD_FROM_POINT_KEY = '__clipboardFromPoint';
export const CLIPBOARD_TO_POINT_KEY = '__clipboardToPoint';

type BuildClipboardSelectionInput = {
  selectedIds: Iterable<string>;
  nodes: Node[];
  edges: Edge[];
};

function isPoint(value: unknown): value is ClipboardPoint {
  if (!value || typeof value !== 'object') return false;
  const point = value as { x?: unknown; y?: unknown };
  return typeof point.x === 'number' && Number.isFinite(point.x) && typeof point.y === 'number' && Number.isFinite(point.y);
}

function pickNodeNumber(node: Node, ...keys: string[]): number | undefined {
  const nodeRecord = node as Node & Record<string, unknown>;
  const attrs = (node.attrs ?? {}) as Record<string, unknown>;
  for (const key of keys) {
    const value = nodeRecord[key] ?? attrs[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return undefined;
}

function getNodeBounds(node: Node): { x: number; y: number; width: number; height: number } {
  const x = pickNodeNumber(node, 'rectX', 'x') ?? node.x;
  const y = pickNodeNumber(node, 'rectY', 'y') ?? node.y;
  const width = pickNodeNumber(node, 'rectW', 'width', 'w') ?? node.width ?? node.w ?? 120;
  const height = pickNodeNumber(node, 'rectH', 'height', 'h') ?? node.height ?? node.h ?? 80;
  return { x, y, width, height };
}

function pointFromNodeAnchor(node: Node, anchor: Edge['fromAnchor'], offset: number | undefined): ClipboardPoint {
  const bounds = getNodeBounds(node);
  const t = typeof offset === 'number' && Number.isFinite(offset) ? Math.max(0, Math.min(1, offset)) : 0.5;

  switch (anchor) {
    case 'top':
      return { x: bounds.x + bounds.width * t, y: bounds.y };
    case 'bottom':
      return { x: bounds.x + bounds.width * t, y: bounds.y + bounds.height };
    case 'left':
      return { x: bounds.x, y: bounds.y + bounds.height * t };
    case 'right':
      return { x: bounds.x + bounds.width, y: bounds.y + bounds.height * t };
    default:
      return { x: bounds.x + bounds.width / 2, y: bounds.y + bounds.height / 2 };
  }
}

function resolveEndpointPoint(
  edge: Edge,
  endpoint: 'from' | 'to',
  nodeById: Map<string, Node>,
): ClipboardPoint {
  const attrs = (edge.attrs ?? {}) as Record<string, unknown>;
  const freePoint = endpoint === 'from' ? attrs.fromPoint : attrs.toPoint;
  if (isPoint(freePoint)) return { ...freePoint };

  const nodeId = endpoint === 'from' ? edge.from : edge.to;
  const node = nodeById.get(nodeId);
  if (node) {
    return pointFromNodeAnchor(
      node,
      endpoint === 'from' ? edge.fromAnchor : edge.toAnchor,
      endpoint === 'from' ? edge.fromOffset : edge.toOffset,
    );
  }

  const fallbackPoint = endpoint === 'from' ? attrs[CLIPBOARD_FROM_POINT_KEY] : attrs[CLIPBOARD_TO_POINT_KEY];
  if (isPoint(fallbackPoint)) return { ...fallbackPoint };
  return { x: 0, y: 0 };
}

export function edgeWithClipboardEndpointPoints(edge: Edge, nodeById: Map<string, Node>): Edge {
  return {
    ...edge,
    attrs: {
      ...(edge.attrs ?? {}),
      [CLIPBOARD_FROM_POINT_KEY]: resolveEndpointPoint(edge, 'from', nodeById),
      [CLIPBOARD_TO_POINT_KEY]: resolveEndpointPoint(edge, 'to', nodeById),
    },
  };
}

export function stripClipboardEndpointPoints(edge: Edge): Edge {
  const attrs = { ...(edge.attrs ?? {}) };
  delete attrs[CLIPBOARD_FROM_POINT_KEY];
  delete attrs[CLIPBOARD_TO_POINT_KEY];
  return { ...edge, attrs };
}

export function buildClipboardSelection(input: BuildClipboardSelectionInput): { nodes: Node[]; edges: Edge[] } {
  const selectedIds = Array.from(input.selectedIds);
  const selectedIdSet = new Set(selectedIds);
  const expandedNodeIds = expandNodeIdsWithGroupMembers(selectedIds, input.nodes);
  const nodeById = new Map(input.nodes.map((node) => [node.id, node]));
  const selectedNodes = input.nodes
    .filter((node) => expandedNodeIds.has(node.id))
    .map((node) => ({ ...node, attrs: node.attrs ? { ...node.attrs } : undefined }));

  const copiedEdgesById = new Map<string, Edge>();
  for (const edge of input.edges) {
    const isContainedByCopiedNodes = expandedNodeIds.has(edge.from) && expandedNodeIds.has(edge.to);
    const isExplicitlySelected = selectedIdSet.has(edge.id);
    if (!isContainedByCopiedNodes && !isExplicitlySelected) continue;
    copiedEdgesById.set(edge.id, edgeWithClipboardEndpointPoints(edge, nodeById));
  }

  return {
    nodes: selectedNodes,
    edges: Array.from(copiedEdgesById.values()),
  };
}

export function hasClipboardContent(data: { nodes?: Node[]; edges?: Edge[] } | null | undefined): boolean {
  return Boolean((data?.nodes?.length ?? 0) > 0 || (data?.edges?.length ?? 0) > 0);
}

export function getClipboardContentMin(data: { nodes: Node[]; edges: Edge[] }): ClipboardPoint | null {
  const points: ClipboardPoint[] = [];

  for (const node of data.nodes) {
    const bounds = getNodeBounds(node);
    if (node.type === 'freehand') {
      const nodePoints = Array.isArray(node.attrs?.points) ? (node.attrs.points as number[]) : [];
      for (let i = 0; i < nodePoints.length - 1; i += 2) {
        const x = Number(nodePoints[i]);
        const y = Number(nodePoints[i + 1]);
        if (Number.isFinite(x) && Number.isFinite(y)) {
          points.push({ x: bounds.x + x, y: bounds.y + y });
        }
      }
    } else {
      points.push({ x: bounds.x, y: bounds.y });
    }
  }

  for (const edge of data.edges) {
    const attrs = (edge.attrs ?? {}) as Record<string, unknown>;
    for (const key of [CLIPBOARD_FROM_POINT_KEY, CLIPBOARD_TO_POINT_KEY, 'fromPoint', 'toPoint']) {
      const point = attrs[key];
      if (isPoint(point)) points.push(point);
    }
  }

  if (points.length === 0) return null;
  return {
    x: Math.min(...points.map((point) => point.x)),
    y: Math.min(...points.map((point) => point.y)),
  };
}
